/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export const newTierKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
export const emptyTier = () => ({ _key: newTierKey(), id: '', threshold: null, discount_pct: 0, label: '' });
/** Garantisce una chiave React stabile per ogni fascia (non viene inviata al server). */
export const withTierKeys = (tiers) => (tiers || []).map((t) => (t._key ? t : { ...t, _key: newTierKey() }));
/** Normalizza le fasce per il payload API (numeri, niente chiavi UI). */
export const serializeTiers = (tiers) => (tiers || []).map((t) => ({
  id: t.id, label: t.label,
  threshold: t.threshold === '' || t.threshold === null || t.threshold === undefined ? null : Number(t.threshold),
  discount_pct: Number(t.discount_pct || 0),
}));

/**
 * IseeTiersEditor — editor riutilizzabile delle fasce ISEE (Brand settings + Installer Wizard).
 *  compact=true  → righe a griglia (etichetta / soglia / sconto)
 *  compact=false → tabella completa con id, aggiungi/rimuovi
 */
export const IseeTiersEditor = ({ tiers, onChange, compact = false, testIdPrefix = 'isee-tier' }) => {
  const rows = withTierKeys(tiers);
  const update = (i, patch) => onChange(rows.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, emptyTier()]);
  const thresholdInput = (t, i, cls) => (
    <Input type="number" placeholder="soglia €" value={t.threshold ?? ''} onChange={(e) => update(i, { threshold: e.target.value === '' ? null : e.target.value })} className={cls} data-testid={`${testIdPrefix}-threshold-${i}`} />
  );
  const pctInput = (t, i, cls) => (
    <Input type="number" min={0} max={100} value={t.discount_pct ?? 0} onChange={(e) => update(i, { discount_pct: e.target.value })} className={cls} data-testid={`${testIdPrefix}-pct-${i}`} />
  );

  if (compact) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-editor`}>
        {rows.map((t, i) => (
          <div key={t._key} className="grid grid-cols-[1fr_120px_90px] gap-2 items-center">
            <Input value={t.label} onChange={(e) => update(i, { label: e.target.value })} className="h-10 rounded-lg" data-testid={`${testIdPrefix}-label-${i}`} />
            {thresholdInput(t, i, 'h-10 rounded-lg')}
            <div className="relative">{pctInput(t, i, 'h-10 rounded-lg pr-6')}<span className="absolute right-2 top-2.5 text-xs text-muted-foreground">%</span></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-editor`}>
      <div className="rounded-xl border border-border/70 overflow-hidden">
        <Table>
          <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>Etichetta</TableHead><TableHead>Soglia €</TableHead><TableHead>Sconto %</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((t, i) => (
              <TableRow key={t._key} data-testid={`${testIdPrefix}-row-${i}`}>
                <TableCell><Input value={t.id || ''} onChange={(e) => update(i, { id: e.target.value })} className="font-mono text-xs" /></TableCell>
                <TableCell><Input value={t.label || ''} onChange={(e) => update(i, { label: e.target.value })} /></TableCell>
                <TableCell>{thresholdInput(t, i, '')}</TableCell>
                <TableCell>{pctInput(t, i, '')}</TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => remove(i)} aria-label="Rimuovi fascia" data-testid={`${testIdPrefix}-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Button size="sm" variant="outline" onClick={add} data-testid="brand-add-tier"><Plus className="h-4 w-4 mr-1" /> Aggiungi fascia</Button>
    </div>
  );
};
